// DealLedger — freee 取引の複式簿記(借方/貸方) ledger 表示 (2026-07-22)
//
// JournalReviewPage (仕訳レビュー) の ledger 表示を共有コンポーネント化したもの。
// 見た目・構造は仕訳レビューと同一 (借方=青 / 貸方=橙 / 行対置 / 差額フッタ)。
// データ変換は utils/journalTransform.dealToJournalGroup (共有済み) を使用する。
// 利用元: PaymentAgingPage の取引明細モーダル (支払年齢表)。

import React from 'react';
import type { JournalGroupData, EntryCellData } from '../utils/journalTransform';

const C = {
  ink: '#1d2a3a', sub: '#647386',
  line: '#d9e0e7', lineDark: '#bdc8d3',
  blueTint: '#f0f6ff', blueLine: '#b9d2ed', orangeTint: '#fff7ee', orangeLine: '#ecd2ae',
  good: '#267a62', goodBg: '#edf8f4', danger: '#b3261e', dangerBg: '#fdecea',
};
const FONT = '"Hiragino Sans","Yu Gothic UI","Meiryo",-apple-system,"Segoe UI",sans-serif';
const NUM = '"SFMono-Regular",Consolas,"Liberation Mono",Menlo,monospace';

const S = {
  ledger: { overflow: 'hidden', border: `1px solid ${C.lineDark}`, borderRadius: 5, fontFamily: FONT, fontSize: 14, color: C.ink } as React.CSSProperties,
  ledgerHead: { minHeight: 50, borderBottom: `1px solid ${C.lineDark}`, background: '#f4f7fa' } as React.CSSProperties,
  lineHead: { display: 'grid', placeItems: 'center', borderRight: `1px solid ${C.line}`, color: C.sub, fontSize: 10, letterSpacing: '.08em', fontWeight: 800 } as React.CSSProperties,
  sideTitle: { display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 } as React.CSSProperties,
  sideStrong: { fontSize: 15, letterSpacing: '.06em', fontWeight: 800 } as React.CSSProperties,
  sideSmallHead: { color: C.sub, fontSize: 11, fontWeight: 600 } as React.CSSProperties,
  amountHead: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '0 13px', borderRight: `1px solid ${C.lineDark}`, color: C.sub, fontSize: 11, letterSpacing: '.06em', fontWeight: 800 } as React.CSSProperties,
  lineNo: { display: 'flex', justifyContent: 'center', paddingTop: 16, borderRight: `1px solid ${C.line}`, color: '#6e8095', fontFamily: NUM, fontSize: 12, fontWeight: 800 } as React.CSSProperties,
  entry: { position: 'relative', minWidth: 0, padding: '10px 13px 9px', borderRight: `1px solid ${C.line}` } as React.CSSProperties,
  entryBlank: { background: '#fbfcfd', borderRight: `1px solid ${C.line}`, color: '#b4c0cc', textAlign: 'center' as const, paddingTop: 30, fontWeight: 700 } as React.CSSProperties,
  metaRow: { display: 'flex', overflow: 'hidden', marginTop: 7, padding: '6px 0 5px', borderTop: '1px solid #edf1f4', borderBottom: '1px solid #edf1f4', color: '#5e7083', whiteSpace: 'nowrap', fontSize: 11, lineHeight: '15px' } as React.CSSProperties,
  entryNote: { overflow: 'hidden', marginTop: 6, color: C.sub, fontSize: 11, fontWeight: 600, textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as React.CSSProperties,
  amountCell: { display: 'flex', flexDirection: 'column' as const, alignItems: 'flex-end', justifyContent: 'center', gap: 6, padding: '8px 13px', borderRight: `1px solid ${C.lineDark}`, background: 'rgba(249,251,253,.72)' } as React.CSSProperties,
  amountBlank: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '8px 13px', borderRight: `1px solid ${C.lineDark}`, background: 'rgba(249,251,253,.72)', color: '#c2cbd4', font: `700 16px ${NUM}` } as React.CSSProperties,
  amount: { color: '#223244', fontFamily: NUM, fontSize: 17, fontWeight: 800, letterSpacing: '-.025em', fontVariantNumeric: 'tabular-nums' } as React.CSSProperties,
  taxCode: { color: '#6c7d8e', fontSize: 10, fontWeight: 800, letterSpacing: '.02em' } as React.CSSProperties,
  groupFoot: { display: 'grid', gridTemplateColumns: '1fr 280px', border: `1px solid ${C.lineDark}`, borderTop: 0, borderRadius: '0 0 5px 5px', background: '#fbfcfd', fontFamily: FONT } as React.CSSProperties,
  groupNote: { padding: '10px 13px', color: C.sub, fontSize: 11, fontWeight: 600, lineHeight: 1.55 } as React.CSSProperties,
};

const ledgerGridSt = (narrow: boolean): React.CSSProperties => ({
  display: 'grid',
  gridTemplateColumns: narrow
    ? '38px minmax(0,1fr) 112px 26px minmax(0,1fr) 112px'
    : '44px minmax(0,1fr) 128px 30px minmax(0,1fr) 128px',
});
const sideHeadSt = (side: 'debit' | 'credit'): React.CSSProperties => ({
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', minWidth: 0, padding: '0 13px', gap: 8,
  borderRight: `1px solid ${side === 'debit' ? C.blueLine : C.orangeLine}`,
  background: side === 'debit' ? C.blueTint : C.orangeTint,
});
const sideStampSt = (side: 'debit' | 'credit'): React.CSSProperties => ({
  display: 'inline-grid', placeItems: 'center', width: 36, height: 23, borderRadius: 3,
  border: '1px solid currentColor', fontSize: 10, letterSpacing: '.08em', fontWeight: 900,
  color: side === 'debit' ? '#33699e' : '#a46922', flex: '0 0 auto',
});
const accountSt = (side: 'debit' | 'credit'): React.CSSProperties => ({
  overflow: 'hidden', fontSize: 15, fontWeight: 800, letterSpacing: '.02em',
  textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  color: side === 'debit' ? '#284f75' : '#7e5320',
});
const ledgerRowSt = (even: boolean): React.CSSProperties => ({
  minHeight: 91, borderBottom: `1px solid ${C.line}`, background: even ? '#fcfdfe' : '#fff',
});
const groupBalanceSt = (ok: boolean): React.CSSProperties => ({
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px 13px',
  borderLeft: `1px solid ${C.line}`, background: ok ? '#f1f8f5' : C.dangerBg,
});

const fmtYen = (n: number) => `¥${n.toLocaleString('ja-JP')}`;
const fmtDate = (s: string | null | undefined) => (s ? s.replace(/-/g, '/') : '—');

// 擬似要素 (橋渡し列) — JournalReviewPage と同一
export const DEAL_LEDGER_CSS = `
.dlg-bridge { position: relative; border-right: 1px solid ${C.lineDark}; background: #f5f7fa; }
.dlg-bridge::after { content: ""; position: absolute; top: 18px; bottom: 18px; left: 50%; border-left: 1px solid #dce4eb; }
.dlg-bridge-head { position: relative; border-right: 1px solid ${C.lineDark}; background: #eef2f6; }
.dlg-bridge-head::before { content: "\\21C4"; position: absolute; inset: 0; display: grid; place-items: center; color: #8092a5; font-weight: 800; }
.dlg-status { display: flex; gap: 8px; align-items: center; color: ${C.good}; font-size: 11px; font-weight: 800; }
.dlg-status::before { content: "\\2713"; display: grid; place-items: center; width: 17px; height: 17px; border: 1px solid #9fdbc7; border-radius: 50%; background: ${C.goodBg}; }
.dlg-status.ng { color: ${C.danger}; }
.dlg-status.ng::before { content: "\\0021"; border-color: #e6b0ac; background: ${C.dangerBg}; }
.dlg-meta-piece { display: inline-flex; min-width: 0; max-width: 42%; padding-right: 8px; margin-right: 8px; border-right: 1px solid #e1e7ec; overflow: hidden; text-overflow: ellipsis; }
.dlg-meta-piece:last-child { margin-right: 0; border-right: 0; }
.dlg-meta-key { flex: 0 0 auto; margin-right: 5px; color: #8998a7; font-size: 10px; font-weight: 800; letter-spacing: .04em; }
.dlg-meta-value { overflow: hidden; font-weight: 700; text-overflow: ellipsis; }
@media (max-width: 1180px) { .dlg-side-sub { display: none; } }
`;

const EntryCell: React.FC<{ cell: EntryCellData | null; side: 'debit' | 'credit' }> = ({ cell, side }) => {
  if (!cell) return <div style={S.entryBlank}>—</div>;
  const metas: Array<[string, string]> = [];
  if (cell.partnerName) metas.push(['取引先', cell.partnerName]);
  if (cell.sectionName) metas.push(['部門', cell.sectionName]);
  if (cell.tagNames && cell.tagNames.length) metas.push(['タグ', cell.tagNames.join('・')]);
  if (cell.kind === 'payment' && cell.paymentDate) metas.push(['決済日', fmtDate(cell.paymentDate)]);
  const noteParts: string[] = [];
  if (cell.itemName) noteParts.push(`品目 ${cell.itemName}`);
  if (cell.description) noteParts.push(`摘要: ${cell.description}`);
  return (
    <div style={S.entry}>
      <div style={accountSt(side)}>{cell.accountName}</div>
      {metas.length > 0 && (
        <div style={S.metaRow}>
          {metas.map(([k, v]) => (
            <span key={k} className="dlg-meta-piece">
              <span className="dlg-meta-key">{k}</span>
              <span className="dlg-meta-value">{v}</span>
            </span>
          ))}
        </div>
      )}
      {noteParts.length > 0 && <div style={S.entryNote}>{noteParts.join('　/　')}</div>}
    </div>
  );
};

const AmountCell: React.FC<{ cell: EntryCellData | null }> = ({ cell }) => {
  if (!cell) return <div style={S.amountBlank}>—</div>;
  return (
    <div style={S.amountCell}>
      <div style={S.amount}>{cell.amount.toLocaleString('ja-JP')}</div>
      <div style={S.taxCode}>{cell.kind === 'detail' ? (cell.taxLabel || '') : cell.kind === 'payment' ? '決済' : '未決済'}</div>
    </div>
  );
};

export const DealLedger: React.FC<{ group: JournalGroupData; narrow?: boolean; showFoot?: boolean }> = ({ group: j, narrow = false, showFoot = true }) => {
  const diff = j.debitTotal - j.creditTotal;
  return (
    <div style={{ fontFamily: FONT }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginBottom: 6 }}>
        <div className={`dlg-status${j.balanced ? '' : ' ng'}`}>
          {j.balanced ? '借方・貸方の合計が一致しています' : `借方と貸方に ${fmtYen(Math.abs(diff))} の差額があります`}
        </div>
      </div>
      <div style={S.ledger} role="table" aria-label={`${j.refNo} 複式仕訳明細`}>
        <div style={{ ...ledgerGridSt(narrow), ...S.ledgerHead }} role="row">
          <div style={S.lineHead}>行</div>
          <div style={sideHeadSt('debit')}>
            <div style={S.sideTitle}>
              <span style={sideStampSt('debit')}>借方</span>
              <strong style={{ ...S.sideStrong, color: '#33699e' }}>借方明細</strong>
              <small className="dlg-side-sub" style={S.sideSmallHead}>費用・資産の増加</small>
            </div>
            <small className="dlg-side-sub" style={S.sideSmallHead}>科目 / 管理属性 / 摘要</small>
          </div>
          <div style={S.amountHead}>金額（税込）</div>
          <div className="dlg-bridge-head" />
          <div style={sideHeadSt('credit')}>
            <div style={S.sideTitle}>
              <span style={sideStampSt('credit')}>貸方</span>
              <strong style={{ ...S.sideStrong, color: '#a46922' }}>貸方明細</strong>
              <small className="dlg-side-sub" style={S.sideSmallHead}>資産の減少・負債の増加</small>
            </div>
            <small className="dlg-side-sub" style={S.sideSmallHead}>科目 / 管理属性 / 摘要</small>
          </div>
          <div style={{ ...S.amountHead, borderRight: 0 }}>金額（税込）</div>
        </div>

        {j.lines.map((l, i) => (
          <div key={l.no} style={{ ...ledgerGridSt(narrow), ...ledgerRowSt(i % 2 === 1), ...(i === j.lines.length - 1 ? { borderBottom: 0 } : {}) }} role="row">
            <div style={S.lineNo}>{String(l.no).padStart(2, '0')}</div>
            <EntryCell cell={l.debit} side="debit" />
            <AmountCell cell={l.debit} />
            <div className="dlg-bridge" />
            <EntryCell cell={l.credit} side="credit" />
            {l.credit
              ? <div style={{ ...S.amountCell, borderRight: 0 }}><div style={S.amount}>{l.credit.amount.toLocaleString('ja-JP')}</div><div style={S.taxCode}>{l.credit.kind === 'detail' ? (l.credit.taxLabel || '') : l.credit.kind === 'payment' ? '決済' : '未決済'}</div></div>
              : <div style={{ ...S.amountBlank, borderRight: 0 }}>—</div>}
          </div>
        ))}
      </div>

      {showFoot && (
        <div style={S.groupFoot}>
          <div style={S.groupNote}>
            <b style={{ color: '#42576e', fontWeight: 800 }}>備考:</b>{' '}
            {j.type === 'income' ? '収入取引' : '支出取引'}
            {j.refNumber ? ` ／ 管理番号 ${j.refNumber}` : ''}
            {` ／ 発生日 ${fmtDate(j.issueDate)}`}
            {j.dueDate ? ` ／ 支払期日 ${fmtDate(j.dueDate)}` : ''}
          </div>
          <div style={groupBalanceSt(j.balanced)}>
            <span style={{ color: j.balanced ? C.good : C.danger, fontSize: 11, fontWeight: 800 }}>差額</span>
            <b style={{ color: j.balanced ? '#1f6550' : C.danger, fontFamily: NUM, fontSize: 15, fontWeight: 900, fontVariantNumeric: 'tabular-nums' }}>
              {fmtYen(Math.abs(diff))}
            </b>
          </div>
        </div>
      )}
    </div>
  );
};

export default DealLedger;
